import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { MessageInfoAttachment } from '../../../api/cmail/cmail.dto';
import { uploadAttachment, saveDraft } from '../../../api/cmail.api';
import { useAPIErrorHandler } from '../../../components/hooks/useAPIErrorHandler';
import useNotifications from '../../../_lib/notifications/useNotifications';
import { queries } from '../../../queries';
import type { FormState } from './useEmailForm';
import type { CleanedFormData } from '../utils/formValidationUtils';
import {
  getReplyToMsgId,
  shouldIncludeMessageCategory,
  normalizeMessageCategoryForAPI,
} from '../utils/emailTypeUtils';

export interface UseAttachmentManagementProps {
  readonly form: FormState;
  readonly setForm: (
    form: FormState | ((prev: FormState) => FormState)
  ) => void;
  readonly currentDraftMsgId: string | null;
  readonly setCurrentDraftMsgId: (value: string | null) => void;
  readonly selectedFromEmail: string;
  /** The name that goes with the From address; see `resolveSenderName`. */
  readonly getSenderName: () => string;
  readonly msgCategory?: string;
  readonly message?: any;
  readonly mode?: 'new' | 'reply' | 'reply-single' | 'forward';
  readonly getEmailType: () => 'NEW' | 'REPLY' | 'FORWARD' | 'FOLLOWUP';
  readonly getReferences: () => string[];
  readonly cleanFormData: (formData: FormState) => CleanedFormData;
}

export const useAttachmentManagement = ({
  form,
  setForm,
  currentDraftMsgId,
  setCurrentDraftMsgId,
  selectedFromEmail,
  getSenderName,
  msgCategory,
  message,
  mode = 'new',
  getEmailType,
  getReferences,
  cleanFormData: cleanFormDataProp,
}: UseAttachmentManagementProps) => {
  const [isUploadingAttachment, setIsUploadingAttachment] =
    React.useState(false);
  const notifications = useNotifications();
  const handleAPIError = useAPIErrorHandler();
  const queryClient = useQueryClient();

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) {
      return;
    }

    setIsUploadingAttachment(true);

    try {
      // If this is a new email and we don't have a msgId, create a draft first
      let msgId = currentDraftMsgId;
      if (!msgId) {
        const cleanedForm = cleanFormDataProp(form);
        const shouldIncludeMsgCategory =
          shouldIncludeMessageCategory(msgCategory);

        const draftResponse = await saveDraft({
          data: {
            to: cleanedForm.to,
            cc: cleanedForm.cc,
            bcc: cleanedForm.bcc,
            subject: cleanedForm.subject,
            body: cleanedForm.body,
            attachments: [],
            from: selectedFromEmail,
            senderName: getSenderName(),
            emailType: getEmailType(),
            replyToMsgId: getReplyToMsgId(message, mode),
            references:
              getReferences().length > 0 ? getReferences() : undefined,
            ...(shouldIncludeMsgCategory && {
              msgCategory: normalizeMessageCategoryForAPI(msgCategory),
            }),
          },
        });

        // Extract msgId from the response
        msgId = draftResponse.data.msgId ?? null;
        setCurrentDraftMsgId(msgId);

        // Use the correct query invalidation pattern when a new draft is created
        queryClient.invalidateQueries(queries.cmail.list._def);
        // Invalidate draft stats to refresh the badge count
        queryClient.invalidateQueries(['draft-stats']);
      }

      if (!msgId) {
        handleAPIError(
          'Failed to create draft for attachment. Please try again.'
        )(new Error('No message id available for attachment upload'));
        return;
      }

      // Upload each file
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('docType', file.type.split('/')[0] || 'attachment');

        const response = await uploadAttachment(msgId, { data: formData });

        return {
          docId: response.data.docId,
          docName: response.data.docName,
          docContentType: response.data.docContentType,
          docSize: response.data.docSize,
          bucketName: response.data.bucketName,
          docTime: response.data.docTime,
          docType: response.data.docType,
          s3Path: response.data.s3Path,
          signedS3Url: response.data.signedS3Url,
        } as MessageInfoAttachment;
      });

      const uploadedAttachments = await Promise.all(uploadPromises);

      // Add uploaded attachments to the form
      setForm((prevForm) => ({
        ...prevForm,
        attachments: [...prevForm.attachments, ...uploadedAttachments],
      }));

      notifications.success(
        `Successfully uploaded ${uploadedAttachments.length} attachment(s)`
      );
    } catch (error: any) {
      handleAPIError('Failed to upload attachment')(error);
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const handleRemoveAttachment = (docId: string) => {
    setForm((prevForm) => ({
      ...prevForm,
      attachments: prevForm.attachments.filter(
        (attachment) => attachment.docId !== docId
      ),
    }));
  };

  return {
    isUploadingAttachment,
    handleFileSelect,
    handleRemoveAttachment,
  };
};
